// Totem composition spec: the declarative form of the structure totems
// (docs/game-design/STRUCTURE-GRAPHICS.md). The canonical document lives in
// ./totems.json (mobile consumes a synced copy under mobile/assets/totems/ —
// see `make totems-sync` and the CI totem-spec-sync guard).
//
// This module is deliberately dependency-free (no DOM, no maplibre): it is
// imported by vite.config.ts for the editor's save middleware, which runs in
// Node.

// Material names the flat-wash layers reference from code — they must always
// exist in a spec's materials list, and the editor treats them as reserved.
export const RESERVED_MATERIALS = ['field', 'dirt'];

const NAME_RE = /^[a-z][a-z0-9_]*$/;
// A decal image starting with '$' is a generic named binding: a slot resolved
// at render time by whatever feature supplies it (unresolved slots render
// nothing in-game and a placeholder in the editor).
const BINDING_RE = /^\$[a-z][a-z0-9_]*$/;

export function isBindingImage(image: string): boolean {
  return image.startsWith('$');
}

/** The binding slot name without the leading '$'. */
export function bindingName(image: string): string {
  return image.slice(1);
}

export interface RectPrism {
  shape: 'rect';
  /** Full extents in meters (w = east–west at rot 0, h = north–south). */
  w: number;
  h: number;
  label?: string;
  /** Offset of the prism center from the anchor, meters east / north. */
  dx?: number;
  dy?: number;
  /** Degrees counterclockwise about the prism's own center, in meter space. */
  rot?: number;
  base?: number;
  top: number;
  pattern?: string;
  color?: string;
}

export interface NgonPrism {
  shape: 'ngon';
  /** Circumradius in meters. */
  r: number;
  sides: number;
  label?: string;
  dx?: number;
  dy?: number;
  /** Vertex phase angle, degrees counterclockwise. */
  rot?: number;
  base?: number;
  top: number;
  pattern?: string;
  color?: string;
}

export type PrismSpec = RectPrism | NgonPrism;

/** A flat image stamped on the ground at an offset from the anchor. */
export interface DecalSpec {
  /** A material name, or a generic '$name' binding slot. */
  image: string;
  /** Ground meters the (square) icon spans. */
  size: number;
  label?: string;
  dx?: number;
  dy?: number;
  rot?: number;
}

export interface TotemTypeSpec {
  /** When set, the whole host footprint is emitted as a flat ground fill —
   *  a listed material name (the farm's tilled 'field') or a plain '#rrggbb'
   *  color — in addition to any prisms. Always flat — never extruded. */
  footprintField?: string;
  prisms: PrismSpec[];
  decals?: DecalSpec[];
}

/** True when a footprintField value is a plain color, not a material. */
export function isColorFill(value: string): boolean {
  return value.startsWith('#');
}

export interface TotemSpecFile {
  version: number;
  /** Texture tiles available to prisms/decals — one PNG per name in
   *  ./totem-materials/ (synced to the mobile assets). */
  materials: string[];
  /** Which totem renders for a structure type, when it isn't the totem of
   *  the same name: structure_type → totem name. Resolution everywhere is
   *  `assignments[type] ?? type` — identity entries are normalized away.
   *  Lets a variant (e.g. farm_v2) be swapped in as data, no code change. */
  assignments: Record<string, string>;
  types: Record<string, TotemTypeSpec>;
}

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function fail(path: string, message: string): never {
  throw new Error(`${path}: ${message}`);
}

function requireNumber(path: string, v: unknown, name: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(path, `${name} must be a finite number, got ${JSON.stringify(v)}`);
  }
  return v;
}

// validateTotemSpec parses an untrusted document into a typed spec, throwing
// a path-qualified Error on the first violation (e.g.
// "types.keep.prisms[3]: top (2) must exceed base (9)"). The mobile client
// enforces the identical rules in totem_spec.dart — keep them in lockstep.
export function validateTotemSpec(raw: unknown): TotemSpecFile {
  if (typeof raw !== 'object' || raw === null) fail('$', 'spec must be an object');
  const doc = raw as Record<string, unknown>;
  if (doc.version !== 2) fail('version', `must be 2, got ${JSON.stringify(doc.version)}`);

  if (!Array.isArray(doc.materials) || doc.materials.length === 0) {
    fail('materials', 'must be a non-empty array of material names');
  }
  const materials: string[] = [];
  for (let i = 0; i < doc.materials.length; i++) {
    const m = doc.materials[i];
    if (typeof m !== 'string' || !NAME_RE.test(m)) {
      fail(`materials[${i}]`, `must match ${NAME_RE}, got ${JSON.stringify(m)}`);
    }
    if (materials.includes(m)) fail(`materials[${i}]`, `duplicate material ${m}`);
    materials.push(m);
  }
  for (const reserved of RESERVED_MATERIALS) {
    if (!materials.includes(reserved)) {
      fail('materials', `must include reserved material '${reserved}' (referenced by the wash layers)`);
    }
  }

  if (typeof doc.types !== 'object' || doc.types === null || Array.isArray(doc.types)) {
    fail('types', 'must be an object of structure types');
  }
  const types = doc.types as Record<string, unknown>;
  if (Object.keys(types).length === 0) fail('types', 'must not be empty');

  const out: Record<string, TotemTypeSpec> = {};
  for (const [typeName, rawType] of Object.entries(types)) {
    const tPath = `types.${typeName}`;
    if (!NAME_RE.test(typeName)) fail(tPath, `type name must match ${NAME_RE}`);
    if (typeof rawType !== 'object' || rawType === null) fail(tPath, 'must be an object');
    const t = rawType as Record<string, unknown>;
    if (t.footprintField !== undefined) {
      if (typeof t.footprintField !== 'string') {
        fail(tPath, 'footprintField must be a material name or a #rrggbb color');
      }
      if (isColorFill(t.footprintField)) {
        if (!COLOR_RE.test(t.footprintField)) {
          fail(tPath, 'footprintField color must be a #rrggbb hex string');
        }
      } else if (!materials.includes(t.footprintField)) {
        fail(
          tPath,
          `footprintField must be a listed material (${materials.join(', ')}) or a #rrggbb color`,
        );
      }
    }
    if (!Array.isArray(t.prisms)) fail(tPath, 'prisms must be an array');

    const prisms: PrismSpec[] = [];
    for (let i = 0; i < t.prisms.length; i++) {
      prisms.push(validatePrism(`${tPath}.prisms[${i}]`, t.prisms[i], materials));
    }
    let decals: DecalSpec[] | undefined;
    if (t.decals !== undefined) {
      if (!Array.isArray(t.decals)) fail(tPath, 'decals must be an array');
      decals = [];
      for (let i = 0; i < t.decals.length; i++) {
        decals.push(validateDecal(`${tPath}.decals[${i}]`, t.decals[i], materials));
      }
    }
    out[typeName] = {
      ...(typeof t.footprintField === 'string'
        ? { footprintField: t.footprintField }
        : {}),
      prisms,
      ...(decals && decals.length > 0 ? { decals } : {}),
    };
  }

  const assignments: Record<string, string> = {};
  if (doc.assignments !== undefined) {
    if (
      typeof doc.assignments !== 'object' ||
      doc.assignments === null ||
      Array.isArray(doc.assignments)
    ) {
      fail('assignments', 'must be an object of structure_type → totem name');
    }
    for (const [st, totem] of Object.entries(doc.assignments as Record<string, unknown>)) {
      const aPath = `assignments.${st}`;
      if (!NAME_RE.test(st)) fail(aPath, `structure type must match ${NAME_RE}`);
      if (typeof totem !== 'string' || !out[totem]) {
        fail(aPath, `must name an existing totem, got ${JSON.stringify(totem)}`);
      }
      if (totem !== st) assignments[st] = totem; // identity entries are redundant
    }
  }
  return { version: 2, materials, assignments, types: out };
}

/** The totem a structure type renders as: its assignment, else the totem of
 *  the same name, else null (renders nothing). */
export function resolveTotem(spec: TotemSpecFile, structureType: string): string | null {
  const name = spec.assignments[structureType] ?? structureType;
  return spec.types[name] ? name : null;
}

function validateDecal(path: string, raw: unknown, materials: string[]): DecalSpec {
  if (typeof raw !== 'object' || raw === null) fail(path, 'must be an object');
  const d = raw as Record<string, unknown>;
  if (d.label !== undefined && typeof d.label !== 'string') {
    fail(path, 'label must be a string');
  }
  if (typeof d.image !== 'string') fail(path, 'image must be a string');
  if (isBindingImage(d.image)) {
    if (!BINDING_RE.test(d.image)) {
      fail(path, `binding image must match ${BINDING_RE}, got ${JSON.stringify(d.image)}`);
    }
  } else if (!materials.includes(d.image)) {
    fail(path, `image must be a listed material or a $binding, got ${JSON.stringify(d.image)}`);
  }
  const size = requireNumber(path, d.size, 'size');
  if (size <= 0) fail(path, `size (${size}) must be positive`);
  for (const opt of ['dx', 'dy', 'rot'] as const) {
    if (d[opt] !== undefined) requireNumber(path, d[opt], opt);
  }
  return {
    ...(typeof d.label === 'string' ? { label: d.label } : {}),
    image: d.image,
    ...(d.dx !== undefined ? { dx: d.dx as number } : {}),
    ...(d.dy !== undefined ? { dy: d.dy as number } : {}),
    size,
    ...(d.rot !== undefined ? { rot: d.rot as number } : {}),
  };
}

function validatePrism(path: string, raw: unknown, materials: string[]): PrismSpec {
  if (typeof raw !== 'object' || raw === null) fail(path, 'must be an object');
  const p = raw as Record<string, unknown>;

  if (p.label !== undefined && typeof p.label !== 'string') {
    fail(path, 'label must be a string');
  }
  for (const opt of ['dx', 'dy', 'rot', 'base'] as const) {
    if (p[opt] !== undefined) requireNumber(path, p[opt], opt);
  }
  const base = (p.base as number | undefined) ?? 0;
  const top = requireNumber(path, p.top, 'top');
  if (base < 0) fail(path, `base (${base}) must not be negative`);
  if (top <= base) fail(path, `top (${top}) must exceed base (${base})`);

  const hasPattern = p.pattern !== undefined;
  const hasColor = p.color !== undefined;
  if (hasPattern === hasColor) {
    fail(path, 'exactly one of pattern or color is required');
  }
  if (hasPattern) {
    if (typeof p.pattern !== 'string' || !materials.includes(p.pattern)) {
      fail(path, `pattern must be a listed material (${materials.join(', ')})`);
    }
  } else if (typeof p.color !== 'string' || !COLOR_RE.test(p.color)) {
    fail(path, 'color must be a #rrggbb hex string');
  }

  if (p.shape === 'rect') {
    const w = requireNumber(path, p.w, 'w');
    const h = requireNumber(path, p.h, 'h');
    if (w <= 0 || h <= 0) fail(path, 'w and h must be positive');
    return {
      shape: 'rect',
      w,
      h,
      ...(typeof p.label === 'string' ? { label: p.label } : {}),
      ...(p.dx !== undefined ? { dx: p.dx as number } : {}),
      ...(p.dy !== undefined ? { dy: p.dy as number } : {}),
      ...(p.rot !== undefined ? { rot: p.rot as number } : {}),
      ...(p.base !== undefined ? { base } : {}),
      top,
      ...(hasPattern ? { pattern: p.pattern as string } : { color: p.color as string }),
    };
  }
  if (p.shape === 'ngon') {
    const r = requireNumber(path, p.r, 'r');
    if (r <= 0) fail(path, 'r must be positive');
    if (typeof p.sides !== 'number' || !Number.isInteger(p.sides) || p.sides < 3 || p.sides > 24) {
      fail(path, 'sides must be an integer between 3 and 24');
    }
    return {
      shape: 'ngon',
      r,
      sides: p.sides,
      ...(typeof p.label === 'string' ? { label: p.label } : {}),
      ...(p.dx !== undefined ? { dx: p.dx as number } : {}),
      ...(p.dy !== undefined ? { dy: p.dy as number } : {}),
      ...(p.rot !== undefined ? { rot: p.rot as number } : {}),
      ...(p.base !== undefined ? { base } : {}),
      top,
      ...(hasPattern ? { pattern: p.pattern as string } : { color: p.color as string }),
    };
  }
  return fail(path, `shape must be "rect" or "ngon", got ${JSON.stringify(p.shape)}`);
}
